import { useState, Fragment, useRef, useEffect, useContext } from "react";
import { Zap, Eye, Pencil } from "lucide-react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McPagination } from "@maersk-global/mds-react-wrapper";
import { Skeleton } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { KPICard } from "./components/KPICard";
import { CABChangeItem } from "./components/CABChangeItem";
import { CABCalendar } from "./components/CABCalendar";
import { PIRCard } from "./components/PIRCard";
import { ChangeReportCardModal } from "./components/ChangeReportCardModal";
import OnboardingRequestsTable from "../AppOnboarding/OnboardingRequestsTable";
import ChangeRestrictionSection from "./components/ChangeRestrictionSection";
import RedGreenChannelTracker from "../RedGreenChannelTracker";
import RedChannelNonCompliantSection from "./components/RedChannelNonCompliantSection";
import { TableContext } from "../../context";
import "./ChangeManagerDashboard.css";

export function ChangeManagerDashboard() {
  const { graphData, isazuretokenfetched, tokenFetched, userRole } = useContext(TableContext);
  const currentUserName = graphData?.displayName || "John Smith";
  const currentUserEmail = graphData?.mail || "";
  const [selectedChange, setSelectedChange] = useState(null);
  const [selectedQueueTab, setSelectedQueueTab] = useState("all");
  const [activeChangeFilter, setActiveChangeFilter] = useState("all");
  const [assignmentMap, setAssignmentMap] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [redGreenLoading, setRedGreenLoading] = useState(true);
  const [queueSearchValue, setQueueSearchValue] = useState("");
  const [appliedChangeNumber, setAppliedChangeNumber] = useState("");
  const queueSectionRef = useRef(null);
  const [queueSortColumn, setQueueSortColumn] = useState("time_remaining");
  const [queueSortDirection, setQueueSortDirection] = useState("descending");
  const [queueCurrentPage, setQueueCurrentPage] = useState(1);
  const [queueTotalPages, setQueueTotalPages] = useState(1);
  const [queueTotalRecords, setQueueTotalRecords] = useState(0);
  const [queueEmergencyCount, setQueueEmergencyCount] = useState(0);
  const [queueNormalCount, setQueueNormalCount] = useState(0);
  const [queueOverdueCount, setQueueOverdueCount] = useState(0);
  const [queueLoading, setQueueLoading] = useState(true);
  const [changeQueueItems, setChangeQueueItems] = useState([]);
  const [queueTotalCounts, setQueueTotalCounts] = useState({});
  const [queueRefreshToken, setQueueRefreshToken] = useState(0);
  const [pendingAssignmentChange, setPendingAssignmentChange] = useState(null);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [pendingHoldStatusChange, setPendingHoldStatusChange] = useState(null);
  const [isSubmittingHoldStatusChange, setIsSubmittingHoldStatusChange] =
    useState(false);
  const queueRecordsPerPage = 50;
  const UNASSIGNED_MANAGER_KEY = "unassigned";

  // Mock KPI Data
  const kpiMetrics = [
    {
      id: "normal-changes",
      title: "Normal Changes",
      primaryValue: queueNormalCount,
      description: "Click to view all normal changes",
      icon: "file",
      accent: "info",
    },
    {
      id: "urgent-changes",
      title: "Urgent Changes",
      primaryValue: queueEmergencyCount,
      description: "Click to view all urgent changes",
      icon: "lightning",
      accent: "urgent",
    },
    {
      id: "overdue-changes",
      title: "Overdue Changes",
      primaryValue: queueOverdueCount,
      description: "Click to view all overdue changes",
      icon: "alert",
      accent: "warn",
    },
  ];
  // Mock CAB Changes
  const cabChanges = [
    {
      changeId: "CHG0501185",
      application: "Flux Application",
      risk: "High",
      cabType: "Standard",
      scheduledTime: "2025-01-25T14:00:00",
      status: "Scheduled",
      weekNumber: "Week 4",
    },
    {
      changeId: "CHG0220138",
      application: "Customer Portal",
      risk: "Medium",
      cabType: "Normal",
      scheduledTime: "2025-01-26T10:00:00",
      status: "Scheduled",
      weekNumber: "Week 4",
    },
    {
      changeId: "CHG0319226",
      application: "Payment Gateway",
      risk: "Low",
      cabType: "Expedited",
      scheduledTime: "2025-01-24T16:30:00",
      status: "Approved",
      weekNumber: "Week 4",
    },
    {
      changeId: "CHG0556432",
      application: "Analytics Dashboard",
      risk: "Medium",
      cabType: "Standard",
      scheduledTime: "2025-01-27T09:00:00",
      status: "Scheduled",
      weekNumber: "Week 5",
    },
  ];

  // Mock PIR Items
  const pirItems = [
    {
      changeId: "CHG0448901",
      application: "Payment Gateway",
      outcome: "Failure",
      incidentsAfterRelease: 2,
      stabilityScore: 65,
      rcaStatus: "In Progress",
      complianceOutcome: "Fail",
      completedDate: "2025-01-22",
    },
    {
      changeId: "CHG0447234",
      application: "Customer Portal",
      outcome: "Success",
      incidentsAfterRelease: 0,
      stabilityScore: 98,
      rcaStatus: "Not Started",
      complianceOutcome: "Pass",
      completedDate: "2025-01-21",
    },
    {
      changeId: "CHG0446789",
      application: "Inventory System",
      outcome: "Partial Success",
      incidentsAfterRelease: 1,
      stabilityScore: 82,
      rcaStatus: "Completed",
      complianceOutcome: "Conditional",
      completedDate: "2025-01-20",
    },
    {
      changeId: "CHG0445123",
      application: "Analytics Dashboard",
      outcome: "Success",
      incidentsAfterRelease: 0,
      stabilityScore: 100,
      rcaStatus: "Not Started",
      complianceOutcome: "Pass",
      completedDate: "2025-01-19",
    },
  ];

  const managerOptions = [
    {
      id: "digamber-singh",
      label: "Digamber Singh",
      email: "digamber.singh@maersk.com",
    },
    {
      id: "prashant-pandey",
      label: "Prashant Pandey",
      email: "prashant.pandey@maersk.com",
    },
    {
      id: "subhraksha-nandy",
      label: "Subhraksha Nandy",
      email: "subhraksha.nandy@maersk.com",
    },
    {
      id: "rutuja-nagpure",
      label: "Rutuja Santosh Nagpure",
      email: "rutuja.nagpure@maersk.com",
    },
    {
      id: "jaime-duran",
      label: "Jaime Duran",
      email: "jaime.duran@maersk.com",
    },
    {
      id: "anshu-jaiswal",
      label: "Anshu Jaiswal",
      email: "anshu.jaiswal@maersk.com",
    },
  ];

  const currentUserEmailNormalized = String(currentUserEmail || "").trim().toLowerCase();
  const changeManagerEmails = managerOptions.map(option => String(option.email).toLowerCase());
  const isChangeManager = changeManagerEmails.includes(currentUserEmailNormalized);

  let queueAssigneeOptions;
  if (isChangeManager) {
    const filteredManagers = managerOptions.filter(option => String(option.email).toLowerCase() !== currentUserEmailNormalized);
    queueAssigneeOptions = [
      { id: "all", label: "All", email: null },
      { id: "my-queue", label: "My Queue", email: currentUserEmail || null },
      ...filteredManagers,
      { id: "unassigned", label: "Unassigned", email: null, countKey: UNASSIGNED_MANAGER_KEY },
    ];
  } else {
    queueAssigneeOptions = [
      { id: "all", label: "All", email: null },
      ...managerOptions,
      { id: "unassigned", label: "Unassigned", email: null, countKey: UNASSIGNED_MANAGER_KEY },
    ];
  }

  const normalizeManagerKey = (value) => String(value || "").trim().toLowerCase();

  const normalizeCountBucket = (bucket) => ({
    all_changes_count: Number(bucket?.all_changes_count) || 0,
    urgent_count: Number(bucket?.urgent_count) || 0,
    normal_count: Number(bucket?.normal_count) || 0,
    overdue_count: Number(bucket?.overdue_count) || 0,
  });

  const getCountBucketForOption = (option) => {
    if (!option) {
      return normalizeCountBucket(null);
    }

    if (option.id === "all") {
      return normalizeCountBucket(queueTotalCounts.all_changes);
    }

    if (option.id === "my-queue") {
      const currentUserKey = normalizeManagerKey(currentUserEmail);
      return normalizeCountBucket(queueTotalCounts[currentUserKey]);
    }

    const managerKeySource = option.countKey ?? option.email;
    const managerKey = normalizeManagerKey(managerKeySource);
    return normalizeCountBucket(queueTotalCounts[managerKey]);
  };

  const selectedQueueOption = queueAssigneeOptions.find(
    (option) => option.id === selectedQueueTab,
  );
  const selectedQueueAssigneeEmail = selectedQueueOption?.email || null;
  const isUnassignedQueueSelected =
    selectedQueueOption?.id === "unassigned";
  const shouldFilterByQueueAssignee =
    isUnassignedQueueSelected || Boolean(selectedQueueAssigneeEmail);
  const selectedQueueCounts = getCountBucketForOption(selectedQueueOption);

  // Queue Filter Pills
  const queueFilterPills = [
    {
      id: "all",
      label: "All Changes",
      count: selectedQueueCounts.all_changes_count,
    },
    {
      id: "normal",
      label: "Normal",
      count: selectedQueueCounts.normal_count,
    },
    {
      id: "urgent",
      label: "Urgent",
      count: selectedQueueCounts.urgent_count,
    },
    {
      id: "overdue",
      label: "Overdue",
      count: selectedQueueCounts.overdue_count,
    },
  ];

  // Queue items are already filtered server-side based on selected query params.
  const filteredQueueItems = changeQueueItems;

  const normalizeChangeType = (value) => {
    if (!value) return "Normal";
    const normalizedValue = String(value).trim().toLowerCase();
    return `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`;
  };

  const normalizeRiskLevel = (value) => {
    if (!value) return "Low";
    const normalizedValue = String(value).trim().toLowerCase();
    if (normalizedValue === "moderate") return "Medium";
    return `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`;
  };

  const getTimeRemainingTone = (value) => {
    const normalizedValue = String(value || "").trim();

    if (!normalizedValue || normalizedValue === "-") {
      return "neutral";
    }

    return normalizedValue.startsWith("-") ? "negative" : "positive";
  };

  const getManagerDisplayName = (email) => {
    if (!email) return "Unassigned";

    if (
      currentUserEmail &&
      String(email).toLowerCase() === String(currentUserEmail).toLowerCase()
    ) {
      return currentUserName;
    }

    const normalizedEmail = String(email).toLowerCase();
    const matchedManager = queueAssigneeOptions.find(
      (option) => String(option.email || "").toLowerCase() === normalizedEmail,
    );

    return matchedManager?.label || email;
  };

  const getDaysPendingMeta = (plannedStartDate) => {
    if (!plannedStartDate) {
      return { daysPending: "-", daysPendingStatus: "neutral" };
    }

    const startDate = new Date(plannedStartDate);
    const currentDate = new Date();
    const dayDiff = Math.floor(
      (currentDate - startDate) / (1000 * 60 * 60 * 24),
    );

    if (dayDiff <= 0) {
      return { daysPending: "-", daysPendingStatus: "neutral" };
    }

    if (dayDiff > 7) {
      return { daysPending: `${dayDiff} days`, daysPendingStatus: "danger" };
    }

    if (dayDiff > 2) {
      return { daysPending: `${dayDiff} days`, daysPendingStatus: "warning" };
    }

    return {
      daysPending: `${dayDiff} day${dayDiff > 1 ? "s" : ""}`,
      daysPendingStatus: "neutral",
    };
  };

  const mapQueueApiItem = (apiItem) => {
    const normalizedType = normalizeChangeType(apiItem.type);
    const normalizedRiskLevel = normalizeRiskLevel(apiItem.risk_impact);
    const isUrgent = ["emergency", "urgent"].includes(
      String(apiItem.type || "").toLowerCase(),
    );
    const isOverdue = apiItem.planned_start_date
      ? new Date(apiItem.planned_start_date) < new Date()
      : false;
    const { daysPending, daysPendingStatus } = getDaysPendingMeta(
      apiItem.planned_start_date,
    );
    const assignedManagerEmail = apiItem.assigned_change_manager || "";
    const assignedManagerName = getManagerDisplayName(assignedManagerEmail);
    const isOnHold =
      String(apiItem.on_hold ?? "false").toLowerCase() === "true";
    const isUrgentFlag =
      String(apiItem.u_change_request_urgent ?? "false").toLowerCase() ===
      "true";

    return {
      changeId: apiItem.change_number || "-",
      type: normalizedType,
      application: apiItem.configuration_item || "-",
      state: apiItem.state || "-",
      shortDescription: apiItem.short_description || "-",
      assignmentGroup: apiItem.assignment_group || "-",
      closeCode: apiItem.close_code || "-",
      // configurationItemClass: apiItem.configuration_item_class || "-",
      changeRequestSourceType: apiItem.change_type || "-",
      environment: apiItem.ci_environment || "-",
      affectedCi: Array.isArray(apiItem.affected_ci)
        ? apiItem.affected_ci.join(", ") || "-"
        : apiItem.affected_ci || "-",
      riskLevel: normalizedRiskLevel,
      raisedBy: apiItem.requested_by || "-",
      plannedStartDate: apiItem.planned_start_date || null,
      plannedEndDate: apiItem.planned_end_date || null,
      timeRemaining: apiItem.time_remaining || "-",
      onHold: isOnHold ? "On Hold" : "Not On Hold",
      onHoldBool: isOnHold,
      daysPending,
      daysPendingStatus,
      slaTimer: "N/A",
      slaStatus: "muted",
      assignedTo: assignedManagerEmail,
      assignedToLabel: assignedManagerName,
      owner: assignedManagerName,
      isUrgent,
      isOverdue,
      uChangeRequestUrgent: isUrgentFlag,
      tags: isUrgent ? ["URGENT"] : [],
    };
  };

  const fetchQueueChanges = async () => {
    if (!tokenFetched) {
      return;
    }

    setQueueLoading(true);

    try {
      const selectedChangeRequestType =
        activeChangeFilter === "normal"
          ? "Normal"
          : activeChangeFilter === "urgent"
            ? "Emergency"
            : null;
      const overdueFlag = activeChangeFilter === "overdue";
      const sortByMap = {
        change_number: "change_number",
        configuration_item: "configuration_item",
        type: "type",
        risk_impact: "risk_impact",
        state: "state",
        short_description: "short_description",
        planned_start_date: "planned_start_date",
        planned_end_date: "planned_end_date",
        assignment_group: "assignment_group",
        requested_by: "requested_by",
        close_code: "close_code",
        configuration_item_class: "configuration_item_class",
        change_type: "change_type",
        ci_environment: "ci_environment",
        affected_ci: "affected_ci",
        on_hold: "on_hold",
        assigned_change_manager: "assigned_change_manager",
        u_change_request_urgent: "u_change_request_urgent",
      };
      const mappedSortBy = sortByMap[queueSortColumn] || "planned_start_date";

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URI}/api/change_data/control_tower_changes`, {
        params: {
          page: queueCurrentPage,
          page_size: queueRecordsPerPage,
          state:'Authorize',
          sort_by: mappedSortBy,
          sort_direction: queueSortDirection,
          ...(overdueFlag ? { overdue: true } : {}),
          ...(appliedChangeNumber
            ? { change_number: appliedChangeNumber }
            : {}),
          ...(shouldFilterByQueueAssignee
            ? {
                change_manager_email: isUnassignedQueueSelected
                  ? "unassigned"
                  : selectedQueueAssigneeEmail,
              }
            : {}),
          ...(selectedChangeRequestType
            ? { type: selectedChangeRequestType }
            : {}),
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
        },
      });

      const result = response.data || {};
      const apiData = Array.isArray(result.data) ? result.data : [];
      const rawTotalCounts = result.total_counts || {};
      const normalizedTotalCounts = Object.entries(rawTotalCounts).reduce(
        (acc, [key, bucket]) => {
          acc[normalizeManagerKey(key)] = normalizeCountBucket(bucket);
          return acc;
        },
        {},
      );
      const allChangesCounts = normalizeCountBucket(
        normalizedTotalCounts.all_changes,
      );

      setChangeQueueItems(apiData.map(mapQueueApiItem));
      setQueueTotalPages(Number(result.total_pages) || 1);
      setQueueTotalRecords(Number(result.total_records) || 0);
      setQueueTotalCounts(normalizedTotalCounts);
      setQueueEmergencyCount(allChangesCounts.urgent_count);
      setQueueNormalCount(allChangesCounts.normal_count);
      setQueueOverdueCount(allChangesCounts.overdue_count);
    } catch (error) {
      setChangeQueueItems([]);
      setQueueTotalPages(1);
      setQueueTotalRecords(0);
      setQueueEmergencyCount(0);
      setQueueNormalCount(0);
      setQueueOverdueCount(0);
      setQueueTotalCounts({});
      toast.error("Failed to load change queue data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    if (!tokenFetched) {
      return;
    }

    fetchQueueChanges();
  }, [
    queueCurrentPage,
    tokenFetched,
    appliedChangeNumber,
    selectedQueueTab,
    activeChangeFilter,
    queueSortColumn,
    queueSortDirection,
    queueRefreshToken,
  ]);

  const handleQueueSearch = () => {
    console.log("Searching for change number:", queueSearchValue);
    const trimmedChangeNumber = queueSearchValue.trim();
    setAppliedChangeNumber(trimmedChangeNumber);
    setQueueCurrentPage(1);
  };

  const fetchPendingApprovals = async () => {
    try {
      const onboardingToken = localStorage.getItem("azure_token");

      if (!isazuretokenfetched || !onboardingToken || !graphData?.displayName) {
        setPendingApprovals([]);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/change_manager_onboarding_requests`,
        {
          headers: {
            Authorization: `Bearer ${onboardingToken}`,
          },
        },
      );

      if (response.status === 200) {
        const result = response.data;

        if (result.data && Array.isArray(result.data.pending_requests)) {
          const normalizedPendingRequests = result.data.pending_requests.map(
            (request) => ({
              ...request,
              approval_type: request.approval_type || "change_manager",
            }),
          );

          setPendingApprovals(normalizedPendingRequests);
        } else {
          setPendingApprovals([]);
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      setPendingApprovals([]);
      toast.error("Failed to load pending approvals data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  useEffect(() => {
    if (!isazuretokenfetched || !graphData?.displayName) {
      return;
    }

    const fetchOnboardingRequestsData = async () => {
      setRequestsLoading(true);
      try {
        await fetchPendingApprovals();
      } finally {
        setRequestsLoading(false);
      }
    };

    fetchOnboardingRequestsData();
  }, [graphData?.displayName, isazuretokenfetched]);

  // useEffect(() => {
  //   // Simulate loading for Red/Green Channel Tracker
  //   const timer = setTimeout(() => {
  //     setRedGreenLoading(false);
  //   }, 1000); // Assume it loads in 1 second

  //   return () => clearTimeout(timer);
  // }, []);

  const queueColumns = [
    { id: "change_number", label: "Change Number", sortable: true },
    { id: "configuration_item", label: "Configuration Item", sortable: true },
    { id: "type", label: "Type", sortable: true },
    { id: "risk_impact", label: "Risk Impact", sortable: true },
    { id: "state", label: "State", sortable: true },
    { id: "short_description", label: "Short Description", sortable: true },
    { id: "planned_start_date", label: "Planned Start Date", sortable: true },
    { id: "planned_end_date", label: "Planned End Date", sortable: true },
    { id: "time_remaining", label: "Time Remaining", sortable: false },
    { id: "assignment_group", label: "Assignment Group", sortable: true },
    { id: "requested_by", label: "Requested By", sortable: true },
    { id: "close_code", label: "Close Code", sortable: true },
    { id: "configuration_item_class", label: "Configuration Item Class", sortable: true },
    { id: "change_type", label: "Change Type", sortable: true },
    { id: "ci_environment", label: "CI Environment", sortable: true },
    { id: "affected_ci", label: "Affected CI", sortable: true },
    { id: "on_hold", label: "On Hold", sortable: true,width: "180px" },
    { id: "assigned_change_manager", label: "Assigned Change Manager", sortable: true,width: "200px" },
    { id: "u_change_request_urgent", label: "Urgent", sortable: true, width: "120px" },
    { id: "action", label: "Action", sortable: false, width: "140px" },
  ];

  const queueTableData = filteredQueueItems.map((item) => ({
    id: item.changeId,
    change_number: item.changeId,
    type: item.type,
    configuration_item: item.application,
    state: item.state,
    short_description: item.shortDescription,
    planned_start_date: item.plannedStartDate,
    planned_end_date: item.plannedEndDate,
    time_remaining: item.timeRemaining,
    assignment_group: item.assignmentGroup,
    close_code: item.closeCode,
    configuration_item_class: item.configurationItemClass,
    change_type: item.changeRequestSourceType,
    ci_environment: item.environment,
    affected_ci: item.affectedCi,
    risk_impact: item.riskLevel,
    requested_by: item.raisedBy,
    on_hold: item.onHold,
    onHoldBool: item.onHoldBool,
    assigned_change_manager: item.assignedTo,
    assigned_change_manager_label: item.assignedToLabel,
    u_change_request_urgent: item.uChangeRequestUrgent ? "true" : "false",
    original: item,
  }));

  const defaultHiddenColumns = [
    "configuration_item_class",
    "close_code",
    "requested_by",
    "change_type",
    "ci_environment",
    "affected_ci",
    "state",
    "short_description",
    "on_hold",
    "assignment_group",
    "risk_impact",
    "type",
    "planned_end_date",
  ];

  const hiddenColumns =
    activeChangeFilter === "overdue"
      ? [...defaultHiddenColumns, "time_remaining"]
      : defaultHiddenColumns;

  const getAssignmentValue = (changeId, fallback) =>
    assignmentMap[changeId] ?? fallback;

  const handleQueueAssignmentChange = (
    changeId,
    currentAssigneeEmail,
    managerEmail,
  ) => {
    if (!managerEmail || managerEmail === currentAssigneeEmail) {
      return;
    }

    setPendingAssignmentChange({
      changeId,
      currentAssignee: getManagerDisplayName(currentAssigneeEmail),
      currentAssigneeEmail,
      managerName: getManagerDisplayName(managerEmail),
      managerEmail,
    });
  };

  const closeAssignmentConfirmation = () => {
    if (isSubmittingAssignment) {
      return;
    }

    setPendingAssignmentChange(null);
  };

  const submitAssignmentChange = async () => {
    if (!pendingAssignmentChange) {
      return;
    }

    if (!pendingAssignmentChange.managerEmail) {
      toast.error("Missing assignee email for reassignment.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }

    setIsSubmittingAssignment(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/change_data/reassign-change`,
        {
          change_id: pendingAssignmentChange.changeId,
          user_email: pendingAssignmentChange.managerEmail,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
          },
        },
      );

      setAssignmentMap((prev) => ({
        ...prev,
        [pendingAssignmentChange.changeId]: pendingAssignmentChange.managerEmail,
      }));

      setQueueRefreshToken((prev) => prev + 1);

      toast.success("Assignment updated successfully", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      setPendingAssignmentChange(null);
    } catch (error) {
      toast.error("Failed to update assignment", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleReviewChange = (change) => {
    setSelectedChange(change);
  };

  const handleOpenHoldStatusConfirmation = (change) => {
    if (!change?.changeId || change.changeId === "-") {
      return;
    }

    const nextOnHoldValue = !Boolean(change.onHoldBool);

    setPendingHoldStatusChange({
      changeId: change.changeId,
      currentStatusLabel: change.onHold,
      nextOnHoldValue,
      nextStatusLabel: nextOnHoldValue ? "On Hold" : "Not On Hold",
    });
  };

  const closeHoldStatusConfirmation = () => {
    if (isSubmittingHoldStatusChange) {
      return;
    }

    setPendingHoldStatusChange(null);
  };

  const submitHoldStatusChange = async () => {
    if (!pendingHoldStatusChange) {
      return;
    }

    const changeIdToUpdate = pendingHoldStatusChange.changeId;
    const nextOnHoldValue = pendingHoldStatusChange.nextOnHoldValue;
    const previousQueueItemsSnapshot = changeQueueItems;

    setChangeQueueItems((prev) =>
      prev.map((item) => {
        if (item.changeId !== changeIdToUpdate) {
          return item;
        }

        return {
          ...item,
          onHoldBool: nextOnHoldValue,
          onHold: nextOnHoldValue ? "On Hold" : "Not On Hold",
        };
      }),
    );

    setIsSubmittingHoldStatusChange(true);

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/api/change_data/update_on_hold_status`,
        null,
        {
          params: {
            change_number: changeIdToUpdate,
            on_hold: nextOnHoldValue,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
          },
        },
      );

      toast.success(
        response?.data?.message ||
          `Status updated to ${pendingHoldStatusChange.nextStatusLabel}`,
        {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        },
      );

      setQueueRefreshToken((prev) => prev + 1);
      setPendingHoldStatusChange(null);
    } catch (error) {
      setChangeQueueItems(previousQueueItemsSnapshot);

      toast.error("Failed to update hold status", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmittingHoldStatusChange(false);
    }
  };

  const handleQueueSortChange = ({ detail: { column, direction } }) => {
    setQueueSortColumn(column);
    setQueueSortDirection(direction);
  };

  const handleQueuePageChange = (event) => {
    setQueueCurrentPage(event.detail);
  };

  useEffect(() => {
    setQueueCurrentPage(1);
  }, [
    selectedQueueTab,
    activeChangeFilter,
    queueSortColumn,
    queueSortDirection,
  ]);

  const scrollToQueueSection = () => {
    if (!queueSectionRef.current) return;
    queueSectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleKpiClick = (metricId) => {
    if (metricId === "normal-changes") {
      setSelectedQueueTab("all");
      setActiveChangeFilter("normal");
    } else if (metricId === "urgent-changes") {
      setSelectedQueueTab("all");
      setActiveChangeFilter("urgent");
    } else if (metricId === "overdue-changes") {
      setSelectedQueueTab("all");
      setActiveChangeFilter("overdue");
      setQueueRefreshToken((prev) => prev + 1);
    } else {
      return;
    }

    window.requestAnimationFrame(scrollToQueueSection);
  };

  // Utility Functions
  const getRiskColor = (risk) => {
    const riskMap = {
      Low: "risk-low",
      Medium: "risk-medium",
      High: "risk-high",
      Emergency: "risk-emergency",
    };
    return riskMap[risk] || "risk-low";
  };

  const getStatusColor = (status) => {
    const statusMap = {
      Pending: "status-pending",
      "Under Review": "status-review",
      Approved: "status-approved",
      Scheduled: "status-scheduled",
      Completed: "status-completed",
      Success: "status-success",
      Failure: "status-failure",
      "Partial Success": "status-partial",
      Pass: "status-pass",
      Fail: "status-fail",
      Conditional: "status-conditional",
      "In Progress": "status-in-progress",
      "Not Started": "status-not-started",
    };
    return statusMap[status] || "status-default";
  };

  const formatQueueDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openChangeInServiceNow = (changeId) => {
    if (!changeId || changeId === "-") {
      return;
    }

    const targetPath = `change_request.do?sysparm_query=number=${changeId}`;
    const serviceNowUrl = `https://maersk.service-now.com/now/nav/ui/classic/params/target/${encodeURIComponent(targetPath)}`;
    window.open(serviceNowUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="change-manager-dashboard">
      {/* Page Title */}
      <div className="dashboard-header">
        <h2 className="dashboard-title">Change Control Tower</h2>
          <p className="dashboard-subtitle">
            Manage change queues, review pending approvals and monitor change health
          </p>
      </div>

      {/* KPI Summary Row */}
      <div className="kpi-grid">
        {queueLoading && queueEmergencyCount === 0 && queueNormalCount === 0 && queueOverdueCount === 0 ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="kpi-skeleton">
              <Skeleton variant="rectangular" height={200} style={{ borderRadius: '8px' }} />
            </div>
          ))
        ) : (
          kpiMetrics.map((metric, idx) => (
            <KPICard
              key={metric.id ?? idx}
              metric={metric}
              onClick={
                metric.id === "normal-changes" ||
                metric.id === "urgent-changes" ||
                metric.id === "overdue-changes"
                  ? () => handleKpiClick(metric.id)
                  : undefined
              }
            />
          ))
        )}
      </div>

      {/* Change Queue Table */}
      <div className="dashboard-card change-queue-card" ref={queueSectionRef}>
        <div className="queue-layout">
          <div className="queue-manager-panel">
            <div className="queue-manager-list" role="listbox" aria-label="Queue selector">
              {queueLoading && queueEmergencyCount === 0 && queueNormalCount === 0 && queueOverdueCount === 0 ? (
                Array.from({ length: queueAssigneeOptions.length }).map((_, idx) => (
                  <Skeleton key={idx} variant="rectangular" height={44} style={{ marginBottom: '0.5rem', borderRadius: '6px' }} />
                ))
              ) : (
                queueAssigneeOptions.map((option) => {
                  const isActive = selectedQueueTab === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`queue-manager-item ${isActive ? "active" : ""}`}
                      onClick={() => {
                        setSelectedQueueTab(option.id);
                        setQueueCurrentPage(1);
                      }}
                    >
                      {option.label} ({getCountBucketForOption(option).all_changes_count})
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="queue-content-panel">
            <div className="queue-filter-row">
              {/* <span className="queue-filter-label">Filter by:</span> */}
              <div className="queue-filter-chips">
                {queueFilterPills.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`queue-chip ${activeChangeFilter === filter.id ? "active" : ""}`}
                    onClick={() => setActiveChangeFilter(filter.id)}
                  >
                    {filter.label}
                    <span className="queue-chip-count">({filter.count})</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="queue-search-row">
              <div className="queue-search-input">
                <McInput
                hiddenlabel
                  label="Change number"
                  placeholder="Search change number"
                  value={queueSearchValue}
                  input={(event) => setQueueSearchValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleQueueSearch();
                    }
                  }}
                  clearbuttonclick={() => {
                    setQueueSearchValue("");
                    setAppliedChangeNumber("");
                    setQueueCurrentPage(1);
                  }}
                  clearbutton
                />
              </div>
              <McButton
                className="queue-search-button"
                click={handleQueueSearch}
                disabled={queueLoading}
              >
                Search
              </McButton>
            </div>

            <div className="change-queue-table-wrapper">
              {queueLoading ? (
                <div style={{ padding: "1rem" }}>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <Skeleton
                      key={`queue-skeleton-${index}`}
                      variant="rectangular"
                      height={44}
                      style={{ marginBottom: "0.75rem", borderRadius: "6px" }}
                    />
                  ))}
                </div>
              ) : (
                <McTable
                  data={queueTableData}
                  columns={queueColumns}
                  datakey="id"
                  fit="small"
                  headersticky
                  hidecolumns={hiddenColumns}
                  customstyles={`
              .mds-table {
                max-height: 50vh !important;
              }
            `}
                  sortmanual
                  sortchange={handleQueueSortChange}
                  sortdefaultcolumnid={queueSortColumn}
                  sortdefaultdirection={queueSortDirection}
                >
                  {queueTableData.length === 0 ? (
                    <div slot="state" className="empty-row">
                      No changes match this filter.
                    </div>
                  ) : (
                    queueTableData.map((row) => (
                      <Fragment key={row.id}>
                        <div slot={`${row.id}_change_number`}>
                          <button
                            type="button"
                            className="change-id-link"
                            onClick={() =>
                              openChangeInServiceNow(row.change_number)
                            }
                          >
                            {row.change_number}
                          </button>
                        </div>
                        <div slot={`${row.id}_type`}>
                          <div className="type-cell">
                            <span className="type-label">{row.type}</span>
                            <div className="type-tags">
                              {row.original.tags?.map((tag) => (
                                <span
                                  key={`${row.change_number}-${tag}`}
                                  className={`queue-tag ${tag === "URGENT" ? "urgent" : tag === "Starts Soon" ? "soon" : ""}`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div slot={`${row.id}_configuration_item`}>{row.configuration_item}</div>
                        <div slot={`${row.id}_state`}>{row.state}</div>
                        <div slot={`${row.id}_short_description`}>
                          {row.short_description}
                        </div>
                        <div slot={`${row.id}_planned_start_date`}>
                          {formatQueueDate(row.planned_start_date)}
                        </div>
                        <div slot={`${row.id}_planned_end_date`}>
                          {formatQueueDate(row.planned_end_date)}
                        </div>
                        <div slot={`${row.id}_time_remaining`}>
                          <span
                            className={`time-remaining-value ${getTimeRemainingTone(row.time_remaining)}`}
                          >
                            {row.time_remaining || "-"}
                          </span>
                        </div>
                        <div slot={`${row.id}_assignment_group`}>
                          {row.assignment_group}
                        </div>
                        <div slot={`${row.id}_ci_environment`}>{row.ci_environment}</div>
                        <div slot={`${row.id}_affected_ci`}>{row.affected_ci}</div>
                        <div slot={`${row.id}_risk_impact`}>
                          <span className={getRiskColor(row.risk_impact)}>
                            {row.risk_impact}
                          </span>
                        </div>
                        <div slot={`${row.id}_requested_by`}>{row.requested_by}</div>
                        <div slot={`${row.id}_close_code`}>{row.close_code}</div>
                        <div slot={`${row.id}_configuration_item_class`}>
                          {row.configuration_item_class}
                        </div>
                        <div slot={`${row.id}_change_type`}>
                          {row.change_type}
                        </div>
                        <div slot={`${row.id}_on_hold`}>
                          <div className="hold-cell">
                            <span
                              className={`hold-pill ${row.on_hold === "On Hold" ? "on-hold" : "not-on-hold"}`}
                            >
                              {row.on_hold}
                            </span>
                            <button
                              type="button"
                              className="hold-edit-button"
                              onClick={() =>
                                handleOpenHoldStatusConfirmation({
                                  changeId: row.change_number,
                                  onHold: row.on_hold,
                                  onHoldBool: row.onHoldBool,
                                })
                              }
                              aria-label={`Edit hold status for ${row.change_number}`}
                              title="Edit hold status"
                              disabled={queueLoading || isSubmittingHoldStatusChange}
                            >
                              <Pencil style={{ width: "0.85rem", height: "0.85rem" }} />
                            </button>
                          </div>
                        </div>
                        <div slot={`${row.id}_assigned_change_manager`}>
                          {(() => {
                            const currentAssigneeEmail = getAssignmentValue(
                              row.change_number,
                              row.assigned_change_manager,
                            );
                            const currentAssigneeLabel =
                              getManagerDisplayName(currentAssigneeEmail) ||
                              row.assigned_change_manager_label ||
                              "Unassigned";

                            return (
                              <span>{currentAssigneeLabel}</span>
                            );
                          })()}
                        </div>
                        <div slot={`${row.id}_u_change_request_urgent`}>
                          <span
                            className={`hold-pill ${
                              String(row.u_change_request_urgent).toLowerCase() ===
                              "true"
                                ? "on-hold"
                                : "not-on-hold"
                            }`}
                          >
                            {String(row.u_change_request_urgent).toLowerCase() ===
                            "true"
                              ? "Urgent"
                              : "Not Urgent"}
                          </span>
                        </div>
                        <div slot={`${row.id}_action`}>
                          {(() => {
                            const currentAssigneeEmail = getAssignmentValue(
                              row.change_number,
                              row.assigned_change_manager,
                            );
                            const isAssignedToCurrentUser =
                              currentUserEmailNormalized &&
                              String(currentAssigneeEmail || "").toLowerCase() ===
                                currentUserEmailNormalized;
                            const canAssignToSelf =
                              isChangeManager &&
                              Boolean(currentUserEmailNormalized) &&
                              !isAssignedToCurrentUser;

                            return (
                              <McButton
                                variant="outlined"
                                appearance="neutral"
                                click={() =>
                                  handleQueueAssignmentChange(
                                    row.change_number,
                                    currentAssigneeEmail,
                                    currentUserEmail,
                                  )
                                }
                                disabled={
                                  !canAssignToSelf ||
                                  queueLoading ||
                                  isSubmittingAssignment
                                }
                              >
                                Pull
                              </McButton>
                            );
                          })()}
                        </div>
                      </Fragment>
                    ))
                  )}
                </McTable>
              )}
              {!queueLoading && queueTotalPages > 1 && (
                <div
                  style={{
                    paddingTop: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <McPagination
                    currentpage={queueCurrentPage}
                    totalpages={queueTotalPages}
                    pagechange={handleQueuePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Primary Content */}
      <div className="main-grid">
        <div className="main-column">
          {requestsLoading ? (
            <div className="dashboard-card">
              <Skeleton variant="rectangular" height={400} style={{ borderRadius: '8px' }} />
            </div>
          ) : (
            <OnboardingRequestsTable
              userRequests={[]}
              pendingApprovals={pendingApprovals}
              title="Change Onboarding Requests"
              onPendingApprovalsRefresh={fetchPendingApprovals}
              showOnlyPendingApprovals
              isChangeManagerDashboard
            />
          )}

          <ChangeRestrictionSection
            tokenFetched={tokenFetched}
            graphData={graphData}
            userRole={userRole}
          />

          {/* <RedChannelNonCompliantSection
            tokenFetched={tokenFetched}
            userRole={userRole}
            userEmail={graphData?.mail}
          /> */}
        </div>
      </div>

{/* CAB Changes & Calendar View */}
{/*
<div className="cab-grid">
  <div className="dashboard-card">
    <h3 className="card-title">Upcoming CAB Changes</h3>
    <div className="cab-items">
      {cabChanges.map((change) => (
        <CABChangeItem
          key={change.changeId}
          change={change}
          getRiskColor={getRiskColor}
          getStatusColor={getStatusColor}
        />
      ))}
    </div>
  </div>

  <CABCalendar />
</div>
*/}

      {/* Red/Green Channel Tracker (Details only) */}
      {/* {redGreenLoading ? (
        <div className="dashboard-card">
          <Skeleton variant="rectangular" height={300} style={{ borderRadius: '8px' }} />
        </div>
      ) : (
        <div className="dashboard-card">
          <RedGreenChannelTracker
            showAnalytics={false}
            addButtonLabel="Onboard Application to Red Channel"
          />
        </div>
      )} */}

      {/* PIR Dashboard */}
      {/* <div className="dashboard-card">
        <h3 className="card-title">PIR Dashboard(to-do)</h3>
        <div className="pir-grid">
          {pirItems.map((pir) => (
            <PIRCard
              key={pir.changeId}
              pir={pir}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>
      </div> */}

      {/* Change Report Card Modal */}
      <ChangeReportCardModal
        selectedChange={selectedChange}
        onClose={() => setSelectedChange(null)}
        getRiskColor={getRiskColor}
      />

      {pendingAssignmentChange && (
        <div
          className="assignment-confirm-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="assignment-confirm-modal">
            <h3 className="assignment-confirm-title">
              Confirm Assignment Change
            </h3>
            <p className="assignment-confirm-text">
              You are about to reassign{" "}
              <strong>{pendingAssignmentChange.changeId}</strong> from{" "}
              <strong>{pendingAssignmentChange.currentAssignee}</strong> to{" "}
              <strong>{pendingAssignmentChange.managerName}</strong>.
            </p>
            <div className="assignment-confirm-actions">
              <button
                type="button"
                className="assignment-confirm-cancel"
                onClick={closeAssignmentConfirmation}
                disabled={isSubmittingAssignment}
              >
                Cancel
              </button>
              <button
                type="button"
                className="assignment-confirm-submit"
                onClick={submitAssignmentChange}
                disabled={isSubmittingAssignment}
              >
                {isSubmittingAssignment ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingHoldStatusChange && (
        <div
          className="assignment-confirm-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="assignment-confirm-modal">
            <h3 className="assignment-confirm-title">Confirm Hold Status Change</h3>
            <p className="assignment-confirm-text">
              You are about to update <strong>{pendingHoldStatusChange.changeId}</strong> from{" "}
              <strong>{pendingHoldStatusChange.currentStatusLabel}</strong> to{" "}
              <strong>{pendingHoldStatusChange.nextStatusLabel}</strong>.
            </p>
            <div className="assignment-confirm-actions">
              <button
                type="button"
                className="assignment-confirm-cancel"
                onClick={closeHoldStatusConfirmation}
                disabled={isSubmittingHoldStatusChange}
              >
                Cancel
              </button>
              <button
                type="button"
                className="assignment-confirm-submit"
                onClick={submitHoldStatusChange}
                disabled={isSubmittingHoldStatusChange}
              >
                {isSubmittingHoldStatusChange ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ height: "5vh" }}></div>
    </div>
  );
}
